import React, { Dispatch, SetStateAction, useState } from 'react'
import { DataGridPro, gridClasses, GridSlots } from '@mui/x-data-grid-pro'
import { MCIssueEntity } from '../../../types/MCEntity'
import { formatDateYYYYMMDD, formatMoney } from './../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import { CustomDataGridToolbar, CustomDataGridToolbarProps } from '../../../components/CustomDataGridToolbar'

type MCIssuesGridComponent = React.FC<{
    mcIssues: MCIssueEntity[]
    setSelectedMCIssue: Dispatch<SetStateAction<MCIssueEntity | null>>
}>

const disableSelectedSellOutlineStyle = {
    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
        outline: 'none',
    },
    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
        outline: 'none',
    },
}

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga emissioner hittades</Typography>
        </Box>
    )
}

const columnDefinitions = (locale: string) => [
    { field: 'name', headerName: 'Aktie', minWidth: 90, flex: 1, disableColumnMenu: true },
    {
        field: 'actionDate',
        headerName: 'Datum',
        width: 100,
        disableColumnMenu: true,
        valueFormatter: (value: string) => (value ? formatDateYYYYMMDD(new Date(value)) : ''),
    },
    {
        field: 'subscriptionPrice',
        headerName: 'Pris',
        width: 80,
        disableColumnMenu: true,
        valueFormatter: (value: string | null) => {
            if (!value) {
                return '-'
            }
            return formatMoney(value, { locale, minDecimals: 2, maxDecimals: 5 })
        },
    },
    /*     {
        field: 'numberOfShares',
        headerName: 'Antal aktier',
        width: 120,
        disableColumnMenu: true,
        valueFormatter: (value: number | null) => {
            if (!value) {
                return '-'
            }
            return formatNumber(value, { locale, decimals: 0 })
        },
    }, */
    {
        field: 'newOld',
        headerName: 'Nya:Gamla',
        width: 100,
        disableColumnMenu: true,
        valueFormatter: (value: undefined, row: MCIssueEntity) => {
            if (!row.newShares || !row.oldShares) {
                return ''
            }
            return `${parseInt(row.newShares)}:${parseInt(row.oldShares)}`
        },
    },
    { field: 'actionComment', headerName: 'Kommentar', minWidth: 150, flex: 1, disableColumnMenu: true },
]

export const MCIssuesGrid: MCIssuesGridComponent = ({ mcIssues, setSelectedMCIssue }) => {
    const [showMatchedIssues, setShowMatchedIssues] = useState(false)
    const [tablePage, setTablePage] = useState(0)
    const { i18n } = useTranslation()
    const locale = i18n.language

    return (
        <Stack>
            <Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showMatchedIssues}
                            onChange={() => setShowMatchedIssues((prev) => !prev)}
                            color='primary'
                            disableRipple
                        />
                    }
                    label='Visa avstämda emissioner'
                />
            </Box>
            <DataGridPro
                rows={mcIssues.filter((issue) => (showMatchedIssues ? showMatchedIssues : !issue.matchedTo))}
                getRowId={(row) => `${row.id}-${row.matchedTo}`}
                pagination
                pageSizeOptions={[25]}
                paginationModel={{
                    pageSize: 25,
                    page: tablePage,
                }}
                onPaginationModelChange={({ page }) => {
                    setTablePage(page)
                }}
                hideFooterSelectedRowCount
                sx={disableSelectedSellOutlineStyle}
                columns={columnDefinitions(locale)}
                onRowClick={(params) => {
                    setSelectedMCIssue(params.row)
                }}
                slots={{
                    noResultsOverlay: CustomNoResultsOverlay,
                    toolbar: CustomDataGridToolbar as GridSlots['toolbar'],
                }}
                initialState={{
                    sorting: {
                        sortModel: [
                            { field: 'actionDate', sort: 'desc' },
                            { field: 'name', sort: 'asc' },
                        ],
                    },
                }}
                showToolbar
            />
        </Stack>
    )
}
